import { Tooltip, Box, TextField, IconButton, CircularProgress, InputAdornment } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppliedFilterMeasure, useAppliedFilterOverflow } from "@/hooks/useAppliedFilterOverflow";
import { useSelector } from "react-redux";
import Table from "@/components/Table/Table";
import { addReferral, addReferralBusiness, approvalReferral, deleteReferralById, getLeadList, getMyReferrals, getReferralBusiness, getReferralUsers, updateReferral, updateReferralBusiness, exportReferralBusiness } from "@/store/slices/ReferralProgramSlice";
import { useAppDispatch } from "@/store/hooks";
import ReferralBusinessDetail from "./components/ReferralBusinessDetail";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import toast from "@/lib/toast";
import EditBusinessReferral from "./components/EditReferralBusiness";
import type { RootState } from "@/store/rootReducer";
import { Button } from "@mui/material";
import FilterComponent from "../../FilterComponent/FilterComponent";
import AppliedFilter from "../../FilterComponent/AppliedFilter";
import StatReferralCard from "../../Dashboard/Dashboard/Card/StatReferralCard";
import RejectReferralBusinessModal from "./components/RejectReferralBusinessModal";
import ReferralService from "@/services/api/referral";
import type { ReferralBusinessItem } from "@/types/ReferralProgramtype";
import { setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID } from "@/lib/constants";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";
import type { showFilters } from "@/pages/FilterComponent/FilterInterface";
import { getStatusColor } from "@/lib/methodUtils";

interface approvalReferralPayload {
    ReasonForRejection: string,
    StatusId: number,
}

const ReferralBusiness = () => {
    const dispatch = useAppDispatch();

    const [pageSelected, setPageSelected] = useState<number>(1);
    const [rowsPerPageValue, setRowsPerPageValue] = useState<number>(10);
    const [modalType, setModalType] = useState<"view" | "edit" | "add" | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState(0);
    const [isDeleteOpen, setIsOpenDeleteModal] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const { myReferrals, referralBusinessResponse, loading } = useSelector((state: RootState) => state.referrals);
    const [selectedRow, setSelectedRow] = useState<ReferralBusinessItem | null>(null);
    const [isRejectLoading, setIsRejectLoading] = useState<boolean>(false);
    const [filterData, setFilterData] = useState<any>({
        status: null,
        date: null
    });
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [referralLink, setReferralLink] = useState("");
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { countryList } = useSelector((state: RootState) => state.customer);
    const { modalOpen } = useSelector((state: RootState) => state.global);


    const params = {
        from: (pageSelected - 1) * rowsPerPageValue + 1,
        to: pageSelected * rowsPerPageValue,
        fromDate: filterData?.fromDate ? filterData?.fromDate.format("YYYY-MM-DD") : null,
        search: debouncedSearch?.trim() ? debouncedSearch : null,
        paymentStatusIds: filterData?.statusIds ? filterData.statusIds.map((item: any) => item.StatusId).join(",")
            : null,
        toDate: filterData?.toDate ? filterData?.toDate.format("YYYY-MM-DD") : null,
    }

    const referralStatusList = [
        { Status: "Pending", StatusId: "1" },
        { Status: "Approved", StatusId: "2" },
        { Status: "Rejected", StatusId: "3" },
        { Status: "Succefull Signups", StatusId: "4" }
    ];


    const HandleDelete = async () => {
        if (!selectedDeleteId) return;
        try {
            setDeleteLoading(true);
            const res = await ReferralService.deleteBusinessReferral(selectedDeleteId);
            if (res?.status === 200) {
                toast.success(res?.Message || "Referral deleted successfully");
                dispatch(getReferralBusiness(params));
                closeDeleteModal();
            }
        } catch (err: any) {
            toast.error(err);
        } finally {
            setDeleteLoading(false);
        }
    }

    const handleOpenDeleteModal = (id: number) => {
        setSelectedDeleteId(id);
        setIsOpenDeleteModal(true);
    }

    const closeDeleteModal = () => {
        setIsOpenDeleteModal(false);
    }

    const HandleView = (row: ReferralBusinessItem) => {
        dispatch(setModalOpen({ id: MODAL_ID.VIEW_REFERRAL_BUSINESS, data: row }));
    }

    const closeModal = () => {
        setModalType(null);
        dispatch(setModalOpen({ id: null, data: null }));
    }


    const handleAddOpen = (data: ReferralBusinessItem | null) => {
        dispatch(setModalOpen({ id: MODAL_ID.REFERRAL_BUSINESS, data: data }));
    }

    const handleSaveReferral = async (formData: any, isEditMode: boolean) => {
        try {
            if (isEditMode) {
                const id = modalOpen?.data?.id;

                await dispatch(updateReferralBusiness({
                    id,
                    data: formData
                })).unwrap();

                toast.success("Updated Successfully");
            } else {
                await dispatch(addReferralBusiness(formData)).unwrap();
                toast.success("Added Succesfully");
            }
            dispatch(getReferralBusiness(params));
            closeModal();
        } catch (err: any) {
            toast.error(err);
        }
    }

    const handleAccept = async (row: ReferralBusinessItem, statusId: number) => {

        const payload = {
            leadId: row?.id,
            paymentStatusId: statusId,
            reason: row?.reason || "",
            referralBusinessId: row?.id,
        }
        try {
            const res = await ReferralService.acceptRejectStatus(payload);
            if (res?.status === 200) {
                toast.success(res?.Message || "Referral Accepted Successfully");
                dispatch(getReferralBusiness(params));
            } else {
                toast.error(res?.Message || "Failed to accept Referral");
            }
        } catch (err) {
            toast.error("Something Went Wrong");
        }
    }

    const handleRejectSave = async (rejectionReason: string) => {
        setIsRejectLoading(true);
        try {
            const payload = {
                leadId: selectedRow?.id,
                statusId: 3,
                reason: rejectionReason,
                referralBusinessId: selectedRow?.id,
            }
            const res = await ReferralService.acceptRejectStatus(payload);
            if (res?.status === 200) {
                toast.success(res?.Message || "Referral Rejected Successfully");
                dispatch(getReferralBusiness(params));
            } else {
                toast.error(res?.Message || "Failed to reject Referral");
            }

        } catch (error) {
            toast.error("Something Went Wrong");
        } finally {
            setIsRejectLoading(false);
            setOpenRejectModal(false);
        }
    }

    const handleOpenRejectModal = (row: any) => {
        setOpenRejectModal(true);
        setSelectedRow(row);
    }

    const handleExcelDownload = async () => {
        try {
            const payload = {
                ...params,
                from: 1,
                to: -1,
            }
            await dispatch(exportReferralBusiness(payload)).unwrap();
        } catch (err: any) {
            toast.error(err || "Something Went Wrong");
        }
    }

    const applyFilter = (data: any) => {
        setFilterData(data);
        setPageSelected(1);
        setFilterAnchor(null);
    };

    const ColumnName: any[] = [
        {
            name: "Company Name",
            selector: (row: ReferralBusinessItem) => row?.leadName || "-",
            tooltip: (row: ReferralBusinessItem) => row?.leadName || "-",
            witdh: "80px"
        },
        {
            name: "Contact person",
            selector: (row: ReferralBusinessItem) => row?.contactPerson || "-",
        },
        {
            name: "Referred By",
            selector: (row: ReferralBusinessItem) => row?.referralUserName || "-",
        },

        {
            name: "Transactional value",
            selector: (row: ReferralBusinessItem) => row?.transactionValue !== null && row?.transactionValue !== undefined ? `₹${row.transactionValue}` : "-",
        },
        {
            name: "Referral Percentage(%)",
            selector: (row: ReferralBusinessItem) => row?.referralPercentage || "-",
        },
        {
            name: "Referral Reward",
            selector: (row: ReferralBusinessItem) => row?.referralReward !== null && row?.referralReward !== undefined ? `₹${row.referralReward}` : "-",
        },
        {
            name: "Details",
            selector: (row: any) => (
                <span
                    style={{ color: "#3f51b5", cursor: "pointer" }}
                    onClick={() => HandleView(row)}
                >
                    View
                </span>
            ),
        },
        {
            name: "Payment Status",
            selector: (row: ReferralBusinessItem) => (
                <span className={getStatusColor(row?.paymentStatus)}>
                    {row.paymentStatus || "-"}
                </span>
            ),

        },
        {
            name: "Action",
            selector: (row: ReferralBusinessItem) =>
                row.paymentStatusId === 1 &&
                (<div className="flex gap-1">
                    <Tooltip title="Edit">
                        <IconButton onClick={() => handleAddOpen(row)}>
                            <img src="/assets/media/edit.svg" alt="" width={"15px"} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleOpenDeleteModal(row?.id)}>
                            <img src="/assets/media/delete.svg" alt="" width={"15px"} />
                        </IconButton>
                    </Tooltip>
                </div>
                ),
        },
        {
            name: "",
            selector: (row: ReferralBusinessItem) =>

                row.paymentStatusId === 1 &&
                (<span style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button>
                        <img src="./assets/media/attendance/accept.svg"
                            alt=""
                            width="20px"
                            onClick={() => handleAccept(row, 2)}
                            style={{ cursor: "pointer" }} />
                    </button>
                    <button onClick={() => handleOpenRejectModal(row)}>
                        <img src="./assets/media/attendance/reject.svg"
                            alt=""
                            width="20px"
                            style={{ cursor: "pointer" }} />
                    </button >
                </span >)


        }
    ];

    const CardsData = [
        {
            title: "Total Companies",
            value: referralBusinessResponse?.referralBusinessCount?.totalLeads || 0,
            icon: "/assets/media/icons/companies.svg",
        },
        {
            title: "Total Businesses",
            value: referralBusinessResponse?.referralBusinessCount?.totalBusiness || 0,
            icon: "/assets/media/icons/handshake.svg",
        },
        {
            title: "Transaction value",
            value: referralBusinessResponse?.referralBusinessCount?.totalTransactionValue || 0,
            icon: "assets/media/icons/transaction.svg",
        },
        {
            title: "Referral Rewards",
            value: referralBusinessResponse?.referralBusinessCount?.totalReferralReward || 0,
            icon: "/assets/media/icons/reward.svg",
        },
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText.trim());
            setPageSelected(1);
        }, 500);
        return () => clearTimeout(timer);

    }, [searchText]);

    useEffect(() => {
        dispatch(getReferralBusiness(params));
    }, [pageSelected, rowsPerPageValue, dispatch, filterData, debouncedSearch]);

    useEffect(() => {
        dispatch(getReferralUsers());
    }, []);

    const handleFilterClick = (e: React.MouseEvent<HTMLElement>) => {
        setFilterAnchor((prev) =>
            prev ? null : e.currentTarget
        );
    };

    const showFilters: showFilters = {
        status: { show: true, mode: "multi" },
        date: { show: true, type: "date" },
    };

    const appliedFilterProps = useMemo(
        () => ({
            filterData,
            onClearFilter: setFilterData,
            showFilters: showFilters,
        }),
        [filterData, showFilters],
    );

    const { chipsOverflow, headerRef, filterBtnRef, rightRef, measureRef } =
        useAppliedFilterOverflow({ watch: filterData });

    return (
        <>
            <div style={{ position: "relative" }}>
                <AppliedFilterMeasure measureRef={measureRef} {...appliedFilterProps} />

                <div
                    ref={headerRef}
                    className="flex items-center justify-between p-2 border-b border-gray-200"
                >
                    <div className="flex items-center gap-4 min-w-0">
                        <div ref={filterBtnRef}>
                            <Tooltip title="filter">
                                <Button variant="contained" onClick={handleFilterClick}>
                                    Filter
                                </Button>
                            </Tooltip>
                        </div>
                        {!chipsOverflow && <AppliedFilter {...appliedFilterProps} />}
                    </div>

                    <div ref={rightRef} className="flex gap-5 shrink-0">
                        <TextField
                            margin="dense"
                            label="Search"
                            fullWidth
                            variant="standard"
                            value={searchText}
                            placeholder="Search"
                            onChange={(e) => setSearchText(e.target.value)}
                        />

                        <Button
                            variant="contained"
                            sx={{
                                flexShrink: 0,
                                alignSelf: "center",
                                height: "fit-content"
                            }}
                            onClick={() => handleAddOpen(null)}
                        >
                            Add
                        </Button>
                        <ButtonLoader
                            loading={loading.export}
                            sx={{
                                flexShrink: 0,
                                alignSelf: "center",
                                height: "fit-content"
                            }}
                            variant="contained"
                            onClick={handleExcelDownload}
                        >
                            Export
                        </ButtonLoader>
                    </div>
                </div>

                {chipsOverflow && (
                    <div className="pt-3">
                        <AppliedFilter {...appliedFilterProps} />
                    </div>
                )}

                <FilterComponent
                    anchorEl={filterAnchor}
                    onClose={() => setFilterAnchor(null)}
                    onApply={applyFilter}
                    filterName="referral"
                    width="400px"
                    showFilters={showFilters}
                    list={{
                        statusList: referralStatusList
                    }}
                    appliedFilter={filterData}
                />
            </div>

            <div className="grid grid-cols-4 justify-center  p-8 gap-4" style={{ backgroundColor: "#fbfbfc" }}>
                {
                    CardsData.map((card) => (
                        <StatReferralCard
                            key={card.title}
                            value={card.value}
                            icon={card.icon}
                            title={card.title}
                            iconSize={55}
                        />
                    ))
                }

            </div>
            <div className="p-3">
                <Table
                    columns={ColumnName}
                    maxHeight="calc(100vh - 385px)"
                    data={referralBusinessResponse?.referralBusinessList?.results || []}
                    paginationProps={{
                        isPagination: true,
                        totalCount: referralBusinessResponse?.referralBusinessList?.totalRecords || 0,
                        pageSelected: pageSelected,
                        setPageSelected: setPageSelected,
                        rowsPerPageValue: rowsPerPageValue,
                        setRowsPerPageValue: setRowsPerPageValue,
                    }}
                    loading={loading?.referral}
                />
                <ReferralBusinessDetail
                    viewModalOpen={modalOpen.id === MODAL_ID.VIEW_REFERRAL_BUSINESS}
                    closeModal={closeModal}
                />
                <ConfirmModal
                    modalOpen={isDeleteOpen}
                    closeModal={closeDeleteModal}
                    title={"Delete"}
                    description={"Are you sure you want to delete this referral?"}
                    buttonText={"Delete"}
                    onButtonClick={HandleDelete}
                    isVariant={true}
                    waitDescription="Deleting..."
                />
                <EditBusinessReferral
                    viewModalOpen={modalOpen.id === MODAL_ID.REFERRAL_BUSINESS}
                    closeModal={closeModal}
                    onSave={handleSaveReferral}
                    loading={loading.saveBusiness}
                />
                <RejectReferralBusinessModal
                    rejectModalOpen={openRejectModal}
                    rejectModalClose={() => setOpenRejectModal(false)}
                    reason={selectedRow?.reason || ""}
                    onSave={handleRejectSave}
                    isRejectLoading={isRejectLoading}
                />

            </div>
        </>
    )
}

export default ReferralBusiness

